import { FC, useEffect, useState } from 'react';

import classNames from 'classnames';

import { Heading, Pressable, Button } from '../../../shared/ui';

import { ColorGrid } from './color-grid';
import { ColorPalette } from './color-palette';

const INITIAL_COLORS = ['#D90018', '#42AA02', '#93532C', '#FFCD0B', '#51007E', '#013C7E', '#817F85', '#010001'];

export const ColorCodeClient: FC<{
  sendColorCodeResult?: (colors: string[]) => void;
  /** Мобильный 1:1: инструмент на весь экран (заголовок лежит в верхней полосе сцены), компактные ячейки */
  mobile?: boolean;
}> = ({ sendColorCodeResult, mobile = false }) => {
  const [availableColors, setAvailableColors] = useState(INITIAL_COLORS);
  const [selectedColors, setSelectedColors] = useState<(string | null)[]>(Array(8).fill(null));
  const [draggedColor, setDraggedColor] = useState<string | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  const [tapSelectedColor, setTapSelectedColor] = useState<string | null>(null);
  const [lastTapTime, setLastTapTime] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsNarrowScreen(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = mobile || isNarrowScreen;

  // === DESKTOP (drag & drop) ===
  const handleDragStart = (color: string) => {
    if (!isMobile) setDraggedColor(color);
  };
  const handleDragOver = (e: React.DragEvent) => {
    if (!isMobile) e.preventDefault();
  };
  const handleDrop = (index: number) => {
    if (isMobile || !draggedColor) return;
    // Проверяем, занят ли слот
    if (selectedColors[index] !== null) return;

    setSelectedColors((prev) => {
      const updated = [...prev];
      updated[index] = draggedColor;

      return updated;
    });
    setAvailableColors((prev) => prev.filter((c) => c !== draggedColor));
    setDraggedColor(null);
    setHoverIndex(null);
  };

  const handleDoubleClick = (index: number) => {
    const color = selectedColors[index];
    if (!color) return;
    setAvailableColors((prev) => [...prev, color]);
    setSelectedColors((prev) => prev.map((c, i) => (i === index ? null : c)));
  };

  const handleTapColor = (color: string) => {
    if (!isMobile) return;
    setTapSelectedColor((prev) => (prev === color ? null : color));
  };

  const handleTapSlot = (index: number) => {
    if (!isMobile) return;

    const now = Date.now();

    if (lastTapTime && now - lastTapTime < 300) {
      const color = selectedColors[index];
      if (color) {
        setAvailableColors((prev) => [...prev, color]);
        setSelectedColors((prev) => prev.map((c, i) => (i === index ? null : c)));
      }
      setLastTapTime(null);

      return;
    }

    setLastTapTime(now);

    if (tapSelectedColor) {
      // Проверяем, занят ли слот
      if (selectedColors[index] !== null) {
        setTapSelectedColor(null);
        return;
      }

      setSelectedColors((prev) => {
        const updated = [...prev];
        updated[index] = tapSelectedColor;

        return updated;
      });
      setAvailableColors((prev) => prev.filter((c) => c !== tapSelectedColor));
      setTapSelectedColor(null);
    }
  };

  const submitSelection = () => {
    const colors = selectedColors.filter((c): c is string => c !== null);
    sendColorCodeResult?.(colors);
    setIsSubmitted(true);
  };

  const isComplete = selectedColors.every((c) => c !== null);

  return (
    <div
      className={classNames(
        'h-full w-full',
        mobile ? 'flex flex-col' : 'sm:w-[472px] lg:w-[711px] flex flex-row pr-[0.25rem] rounded-card',
      )}
    >
      <div
        className={classNames(
          'w-full flex flex-col text-white overflow-y-auto white-scroll',
          mobile ? 'min-h-0 flex-1 bg-ink-muted px-4 pb-0 pt-1' : 'bg-ink-muted p-6',
        )}
      >
        {!mobile && (
          <Heading tone="inverse" size="lg" className="mb-5">
            Цветовой код
          </Heading>
        )}
        <p className={classNames('leading-relaxed', mobile ? 'mb-3 text-ui' : 'mb-6 text-ui lg:text-body-xl')}>
          {mobile
            ? 'Разложите 8 цветов от самого приятного сейчас до самого неприятного. Нажмите на цвет, затем на пустую ячейку. Чтобы вернуть цвет, дважды нажмите на ячейку.'
            : 'Разложите эти 8 цветов по порядку: от самого приятного для вас в данный момент до самого неприятного. Доверьтесь первому эмоциональному восприятию здесь и сейчас.'}
        </p>
        {!isMobile && (
          <>
            <ColorPalette colors={availableColors} onDragStart={handleDragStart} />

            <ColorGrid
              selectedColors={selectedColors}
              onDrop={handleDrop}
              onDoubleClick={handleDoubleClick}
              onDragOver={handleDragOver}
              hoverIndex={hoverIndex}
              setHoverIndex={setHoverIndex}
            />
          </>
        )}
        {isMobile && (
          <>
            <div className={classNames('grid grid-cols-4 grid-rows-2 gap-2', mobile ? 'mb-3' : 'mb-4')}>
              {Array.from({ length: 8 }, (_, index) => {
                const color = availableColors[index];
                if (!color) {
                  return (
                    <div
                      key={`empty-${index}`}
                      className={classNames('w-full', mobile ? 'aspect-[5/4]' : 'aspect-square')}
                    />
                  );
                }
                return (
                  <Pressable
                    key={color}
                    aria-label={`Цвет ${index + 1}`}
                    aria-pressed={tapSelectedColor === color}
                    onClick={() => handleTapColor(color)}
                    className={`w-full ${
                      mobile ? 'aspect-[5/4]' : 'aspect-square'
                    } rounded-control transition-transform duration-150 ${
                      tapSelectedColor === color ? 'scale-110 ring-4 ring-white' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                );
              })}
            </div>

            <div className={classNames('grid grid-cols-4 grid-rows-2 gap-2 w-full', mobile ? 'mb-3' : 'mb-6')}>
              {selectedColors.map((color, i) => (
                <Pressable
                  key={i}
                  aria-label={`Место ${i + 1}`}
                  onClick={() => handleTapSlot(i)}
                  className={`w-full ${mobile ? 'aspect-[5/4]' : 'aspect-square'} rounded-control border-2 ${
                    color ? 'border-none' : 'border-ink-subtle'
                  }`}
                  style={{ backgroundColor: color || 'transparent' }}
                >
                  {!color && <span className="text-heading-md text-white/70">{i + 1}</span>}
                </Pressable>
              ))}
            </div>
          </>
        )}
        {isSubmitted && mobile && <p className="pb-4 text-center text-ui text-white/80">Выбор отправлен психологу</p>}
        {!isSubmitted && (
          <div
            className={classNames(
              'w-full flex justify-center mx-auto',
              mobile ? 'sticky bottom-0 mt-auto bg-ink-muted pb-3 pt-2' : 'pt-1 pb-4',
            )}
          >
            <Button size="lg" fullWidth disabled={!isComplete} onClick={submitSelection}>
              Выбрать
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
